<template lang="pug">
  .arrow-steps-wrapper
    .arrow-steps
      .arrow-step(
        v-for="(status, step) in visibleStatuses"
        :key="step"
        :class="isSentStepActiveComplete(step)"
      )
        Icon.icon-size-xl(
          :name="stepIcon(step)"
          :class="['step-icon', `icon-${stepIcon(step).replace('/', '-')}`]"
        )
        h4.step-label {{ __(stepTitle(step)) }}
        span.arrow-date(v-html="stepDateDisplay(step) || '- -'")
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { checkIfWeNeedToScroll, formatDate, shippingStatuses, type StepKey } from '../helpers'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps({
  currentPackage: {
    type    : Object,
    default : null,
  },
})

const rogue = useRogue()

/**
 * Return current package actual or estimated delivery date based on current package
 * @return {Object}
 */
const currentPackageActiveDeliveryDate = computed<string>(() => {
  const {
    deliveryDate,
    estimatedDeliveryDate,
    estimatedDeliveryTo,
    estimatedDeliveryFrom,
  } = props.currentPackage

  if (deliveryDate) {
    return formatDate(deliveryDate, rogue.l10n.locale)
  }

  if (estimatedDeliveryDate) {
    return `${formatDate(estimatedDeliveryDate, rogue.l10n.locale, false)} By end of day`
  }

  if (estimatedDeliveryTo) {
    return `${formatDate(estimatedDeliveryFrom, rogue.l10n.locale, false)} - ${formatDate(estimatedDeliveryTo, rogue.l10n.locale, false)}`
  }

  return formatDate(estimatedDeliveryFrom, rogue.l10n.locale, false)
})

/**
 * Return the shipping statuses
 * @return {Object}
 */
const statuses = computed(() => {
  const { packageType, items } = props.currentPackage || {}
  
  if (packageType === 'virtual') {
    const { received, processed, delivered } = shippingStatuses
    
    return { received, processed, delivered }
  }
  
  if (items && items.length === 0) {
    const { received, processed } = shippingStatuses
    
    return { received, processed }
  }

  return shippingStatuses
})

const visibleStatuses = computed(() => {
  return Object.fromEntries(Object.entries(statuses.value).filter(([status]) => isSentStepVisible(status as StepKey)))
})

/**
 * This method determines based on steps data
 * whether the sent step is active or has been completed
 * which gives us our green or red underlines
 * @param  {String} sentStep
 * @return {String} State
 */
function isSentStepActiveComplete(sentStep: StepKey) {
  if (!props.currentPackage) {
    return ''
  }
  const { status } = props.currentPackage

  if (!status) {
    return ''
  }
  const currentStep = shippingStatuses[status as StepKey]

  if (!currentStep) {
    return ''
  }

  const sentStepDateKey = shippingStatuses[sentStep].dateKey

  if (
    currentStep
    && stepDate(sentStep)
    && Object.prototype.hasOwnProperty.call(props.currentPackage, sentStepDateKey)
    && (
      currentStep.completedArray.indexOf(sentStep) > -1
      || status === sentStep
    )
  ) {
    if (status === sentStep) {
      return 'active'
    }

    return 'complete'
  }

  return ''
}

/**
 * This method determines whether the sent step is visible
 * @param  {String} sentStep
 * @return {Boolean}
 */
function isSentStepVisible(sentStep: StepKey) {
  const { packageType } = props.currentPackage
  const step = shippingStatuses[sentStep]

  if (step.visibility.indexOf('*') > -1) {
    return true
  }

  return step.visibility.indexOf(packageType || 'regular') > -1
}

/**
 * Return the step title based on the selected package status
 * @param {String} sentStep
 * @return {String}
 */
function stepTitle(sentStep: StepKey) {
  const { isPickup, status, deliveryDate, deliveryDateFormatted } = props.currentPackage
  const step = shippingStatuses[sentStep]

  if (sentStep === 'shipped' && isPickup) {
    return 'Ready For Pickup'
  }
  if (sentStep === 'delivered') {
    if (isPickup && status === 'shipped') {
      return 'Pickup Deadline'
    }

    if (!isPickup || status !== 'shipped') {
      if (isPickup) {
        return 'Picked Up'
      }

      if (deliveryDateFormatted || deliveryDate) {
        return 'Delivered'
      }

      return 'Est Delivery'
    }
  }

  return step.title
}

/**
 * Return the corresponding icon for each step
 * @param {String} sentStep
 * @return {String} icon
 */
function stepIcon(sentStep: StepKey) {
  const step = shippingStatuses[sentStep]
  if (sentStep === 'shipped' && props.currentPackage.isPickup) {
    return 'rss/rfp'
  }

  return step.icon
}

/**
 * Determine the date for each step. Returns a placeholder
 * if date is not available
 * @param {String} sentStep
 * @return {String}
 */
function stepDate(sentStep: StepKey) {
  const { isPickup, pickupDeadline, status } = props.currentPackage
  const step = shippingStatuses[sentStep]

  let date = formatDate(props.currentPackage[step.dateKey], rogue.l10n.locale)
  if (sentStep === 'delivered') {
    if (isPickup && status === 'shipped') {
      date = formatDate(pickupDeadline, rogue.l10n.locale, false)
    } else if (!isPickup || status !== 'shipped') {
      date = currentPackageActiveDeliveryDate.value
    }
  }

  return date
}

/**
 * Determine if the date should be displayed for a step
 * @param {String} sentStep
 * @return {String}
 */
function stepDateDisplay(sentStep: StepKey) {
  const { rssSuccess } = props.currentPackage
  
  // Don't show processed date if RSS failed (400 error)
  if (sentStep === 'processed' && rssSuccess === false) {
    return null
  }
  
  return stepDate(sentStep)
}

onMounted(() => {
  checkIfWeNeedToScroll()
})
</script>

<style lang="scss" scoped>
  $arrow-height : 90px;
  $arrow-divide-angle: 16px;
  $half-arrow-height: 45px;
  $arrow-step-color : $color-gray-light;
  $arrow-wrapper-background : $color-gray-x-light;
  $angle-ratio : 5.5;

  .arrow-steps-wrapper {
    background : $arrow-wrapper-background;
    overflow   : auto;
    margin     : 0 $space-xxs;

    @media (min-width : $desktop) {
      width : 100%;
    }

    .arrow-steps {
      display : flex;

      .arrow-step {
        display          : flex;
        justify-content  : center;
        align-items      : center;
        flex-flow        : column;
        flex-grow        : 1;
        text-align       : center;
        cursor           : default;
        margin           : 0 $spacing-xl;
        padding          : $space-xxxs $space-s $space-xxxs $space-xl;
        height           : $arrow-height;
        position         : relative;
        background-color : $arrow-step-color;
        user-select      : none;
        transition       : background-color $duration-quick ease;
        border-bottom    : $space-xxxs solid $arrow-step-color;

        .arrow-date {
          white-space : pre;
        }

        &:after, &:before {
          content       : " ";
          position      : absolute;
          top           : 0;
          right         : -$arrow-divide-angle;
          width         : 0;
          height        : 0;
          border-top    : $half-arrow-height solid transparent;
          border-bottom : $half-arrow-height solid transparent;
          border-left   : $arrow-divide-angle solid $arrow-step-color;
          z-index       : 1;
          transition    : border-color $duration-quick ease;
        }

        &:before {
          right       : auto;
          left        : 0;
          border-left : $arrow-divide-angle solid $arrow-wrapper-background;
          z-index     : 0;
        }

        &:last-child:after {
          border : none;
        }

        .step-icon {
          display       : inline-flex;
          margin-bottom : $space-xxxs;
          font-size     : $size-xl;
        }

        .step-label {
          color          : $color-black-light;
          font-size      : $size-xs;
          margin         : 0 0 $space-xxxs;
          text-transform : uppercase;
          white-space    : nowrap;
        }

        .arrow-date {
          margin      : 0;
          display     : block;
          font-size   : $size-xxs;
          white-space : nowrap;
        }

        &.complete, &.active {
          border-bottom : $space-xxxs solid $color-green-light;
          color         : $color-black;
          background    : $color-white;

          &:after {
            border-left : $arrow-divide-angle solid $color-white;
          }

          .step-label {
            color : $color-black;
          }
        }

        &.error {
          border-bottom : $space-xxxs solid $color-red;
          background    : $color-white;
        }
      }
    }
  }
</style>
